import React, { useState } from 'react';
import { APPLICATION_LABELS } from '../../utils/serviceApplications';

const QUICK_TIME_SLOTS = [
  '10:00 AM',
  '11:30 AM',
  '01:00 PM',
  '02:30 PM',
  '04:00 PM',
  '05:30 PM',
];

const ScheduleModal = ({ application, isOpen, onClose, onScheduleSuccess }) => {
  const isReschedule =
    application?.status === 'scheduled' || Boolean(application?.scheduled_date && application?.scheduled_time);
  const kind = (application && APPLICATION_LABELS[application.application_type]) || 'Test Drive';

  // Format today's date in YYYY-MM-DD for min date
  const todayStr = new Date().toISOString().split('T')[0];

  const [date, setDate] = useState(application?.scheduled_date || todayStr);
  const [time, setTime] = useState(application?.scheduled_time || '10:00 AM');
  const [customTime, setCustomTime] = useState('');
  const [notes, setNotes] = useState(application?.schedule_notes || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !application) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const selectedTime = customTime.trim() || time.trim();

    if (!date) {
      setError('Please select an appointment date.');
      return;
    }
    if (!selectedTime) {
      setError('Please select or specify an appointment time.');
      return;
    }

    try {
      setLoading(true);
      await onScheduleSuccess(application.id, {
        scheduled_date: date,
        scheduled_time: selectedTime,
        schedule_notes: notes.trim(),
      });
      onClose();
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || err.message || 'Failed to save appointment.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schedule-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="schedule-modal-card"
        onClick={(e) => e.stopPropagation()}
        aria-labelledby="schedule-modal-title"
      >
        <div className="schedule-modal-header">
          <div>
            <div className="schedule-modal-badge">
              <i className="fas fa-calendar-check" />{' '}
              {isReschedule ? 'Reschedule Appointment' : 'New Appointment'}
            </div>
            <h2 id="schedule-modal-title">
              {isReschedule ? `Reschedule ${kind}` : `Schedule ${kind}`}
            </h2>
          </div>
          <button
            type="button"
            className="schedule-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Customer & Vehicle Info Summary */}
        <div className="schedule-customer-summary">
          <div className="summary-col">
            <span className="summary-label">Customer Lead</span>
            <strong className="summary-val">{application.customer_name}</strong>
            <div className="summary-sub">
              <span><i className="fas fa-phone" /> {application.phone}</span>
              {application.email && (
                <span><i className="fas fa-envelope" /> {application.email}</span>
              )}
            </div>
          </div>
          <div className="summary-col">
            <span className="summary-label">Vehicle</span>
            <strong className="summary-val">
              {application.vehicle_variant &&
              !application.vehicle_name?.toLowerCase().includes(application.vehicle_variant.toLowerCase())
                ? `${application.vehicle_name} ${application.vehicle_variant}`.trim()
                : application.vehicle_name || 'Generic / General'}
            </strong>
            {application.city && (
              <div className="summary-sub">
                <span><i className="fas fa-location-dot" /> {application.city}</span>
              </div>
            )}
          </div>
        </div>

        {/* Requested Preference (Customer's initial note) */}
        {(application.preferred_date || application.preferred_time || application.notes) && (
          <div className="schedule-preference-box">
            <span className="pref-heading">Customer Requested Preference:</span>
            <div className="pref-row">
              {application.preferred_date && (
                <span><strong>Date:</strong> {application.preferred_date}</span>
              )}
              {application.preferred_time && (
                <span><strong>Time:</strong> {application.preferred_time}</span>
              )}
              {application.notes && (
                <span><strong>Note:</strong> {application.notes}</span>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="dash-alert" style={{ margin: '16px 0 0 0' }}>
            <i className="fas fa-circle-exclamation" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="schedule-modal-form">
          <div className="schedule-field-group">
            <label htmlFor="sched-date" className="schedule-label">
              <i className="fas fa-calendar-day" /> Appointment Date *
            </label>
            <input
              id="sched-date"
              type="date"
              className="schedule-date-input"
              value={date}
              min={todayStr}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="schedule-field-group">
            <label className="schedule-label">
              <i className="fas fa-clock" /> Appointment Time *
            </label>
            <div className="schedule-time-presets">
              {QUICK_TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={`schedule-preset-btn ${time === slot && !customTime ? 'selected' : ''}`}
                  onClick={() => {
                    setTime(slot);
                    setCustomTime('');
                  }}
                >
                  {slot}
                </button>
              ))}
            </div>

            <div className="schedule-custom-time-wrap">
              <label htmlFor="sched-custom-time" className="schedule-sublabel">
                Or enter custom time:
              </label>
              <input
                id="sched-custom-time"
                type="text"
                className="schedule-text-input"
                placeholder="e.g. 03:15 PM or 15:15"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
              />
            </div>
          </div>

          <div className="schedule-field-group">
            <label htmlFor="sched-notes" className="schedule-label">
              <i className="fas fa-comment-dots" /> Appointment Notes / Dealership Instructions (Optional)
            </label>
            <textarea
              id="sched-notes"
              rows="2"
              className="schedule-textarea"
              placeholder="e.g. Assigned to Main Showroom representative. Bring CNIC and driving license."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="schedule-modal-actions">
            <button
              type="button"
              className="schedule-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="schedule-btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle" />{' '}
                  {isReschedule ? 'Confirm Reschedule' : 'Confirm Schedule'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleModal;
